import React, { useState, useEffect } from 'react';
import './Sidebar.css';
import NewProjectButton from './NewProjectButton';
import { storage } from '../firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { normalizeRotation } from '../videoUpload/rotation';

// The thumbnail box is a 50px SQUARE, so a quarter turn needs no scale
// correction to keep it covered — unlike the 48x30 clip posters in the footage
// list. All four angles are a plain rotate.
const thumbnailRotationClass = (project) => {
  const angle = normalizeRotation(project?.footageRotation);
  return angle ? ` project-thumbnail--rot-${angle}` : '';
};

const VideoThumbnail = ({ storagePath, alt, rotationClass = '' }) => {
  const [thumbUrl, setThumbUrl] = useState(null);

  useEffect(() => {
    if (!storagePath) return;
    let revoked = false;
    const storageRef = ref(storage, storagePath);
    getDownloadURL(storageRef).then((url) => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.preload = 'metadata';
      video.src = url;
      video.addEventListener('loadeddata', () => {
        video.currentTime = 0.001;
      });
      video.addEventListener('seeked', () => {
        const canvas = document.createElement('canvas');
        canvas.width = 100;
        canvas.height = 100;
        const ctx = canvas.getContext('2d');
        const scale = Math.max(100 / video.videoWidth, 100 / video.videoHeight);
        const w = video.videoWidth * scale;
        const h = video.videoHeight * scale;
        ctx.drawImage(video, (100 - w) / 2, (100 - h) / 2, w, h);
        if (!revoked) {
          setThumbUrl(canvas.toDataURL('image/jpeg', 0.7));
        }
      });
    }).catch((err) => {
      console.error('Failed to generate video thumbnail:', err);
    });
    return () => { revoked = true; };
  }, [storagePath]);

  if (thumbUrl) {
    return <img src={thumbUrl} alt={alt} className={`project-thumbnail${rotationClass}`} />;
  }
  return <div className="project-thumbnail-placeholder"></div>;
};

// Outlined speech bubble. Inline rather than an asset so it inherits
// `currentColor` and follows the button's hover/focus states.
const FeedbackIcon = () => (
  <svg
    className="sidebar-feedback-icon"
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M14 9.6a1.6 1.6 0 0 1-1.6 1.6H5.2L2 13.8V3.6A1.6 1.6 0 0 1 3.6 2h8.8A1.6 1.6 0 0 1 14 3.6z" />
  </svg>
);

const isInFlightStatus = (project) => {
  const v = project?.video?.status;
  const e = project?.editedVideo?.status;
  if (v === 'analyzing' || v === 'queued') return 'analyzing';
  if (e === 'processing' || e === 'queued') return 'rendering';
  return null;
};

const Sidebar = (
  {
    projects,
    onProjectSelected,
    selectedProject,
    createProject,
    isCreatingProject,
    user,
    drawerOpen,
    condensed = false,
    onFeedbackClick,
  }
) => {
  const formatDate = (date) => {
    if (!date) {
      return '...';
    }
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  };

  return (
    <div className={`sidebar ${drawerOpen ? 'sidebar-drawer-open' : ''}${condensed ? ' sidebar--condensed' : ''}`}>
      <NewProjectButton handleNewProjectClick={createProject} isCreatingProject={isCreatingProject} />
      <div className="project-list">
        {projects && projects.map((project) => {
          const inFlight = condensed ? isInFlightStatus(project) : null;
          const dotTitle = inFlight === 'analyzing'
            ? 'Analysis in progress'
            : inFlight === 'rendering'
              ? 'Render in progress'
              : null;
          return (
            <div
              key={project.id}
              className={`project-item${selectedProject === project.id ? ' selected' : ''}${condensed ? ' project-item--condensed' : ''}`}
              onClick={() => onProjectSelected(project.id)}
            >
              {!condensed && (
                project.thumbnailUrl ? (
                  <img
                    src={project.thumbnailUrl}
                    alt={project.projectName}
                    className={`project-thumbnail${thumbnailRotationClass(project)}`}
                  />
                ) : project.video?.storagePath ? (
                  <VideoThumbnail
                    storagePath={project.video.storagePath}
                    alt={project.projectName}
                    rotationClass={thumbnailRotationClass(project)}
                  />
                ) : (
                  <div className="project-thumbnail-placeholder"></div>
                )
              )}
              <div className="project-info">
                <span className="project-name">
                  {project.projectName}
                  {inFlight && (
                    <span
                      className={`project-status-dot project-status-dot--${inFlight}`}
                      title={dotTitle}
                      aria-label={dotTitle}
                    />
                  )}
                </span>
                {!condensed && (
                  <span className="project-last-edited">{formatDate(project.createdAt)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Sticky footer. It is a sibling of `.project-list` (which owns
          flex-grow + overflow-y), so it stays pinned to the bottom of the
          sidebar while the project list scrolls underneath it. On <=640px the
          sidebar is the slide-out drawer, so the footer rides along unchanged
          — no separate mobile treatment. */}
      {onFeedbackClick && (
        <div className="sidebar-footer">
          <button
            type="button"
            className="sidebar-feedback-button"
            onClick={onFeedbackClick}
          >
            <FeedbackIcon />
            <span>Feedback</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
